import {
  ClearOutlined,
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import React, { ReactElement, useEffect, useState } from 'react';
import GenericModal from '@portal/components/GenericModal/GenericModal';
import { FiltersFields, SelectOptions } from '@portal/models/module';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import { translate } from '@portal/services/i18n';
import { AdvancedFilterType } from '@portal/models/enumerators';
import AdvancedSelect from '@portal/components/AdvancedSelect/AdvancedSelect';
import { removeSpecialChars, unmaskField } from '@portal/services/masks';
import moment, { Moment } from 'moment';
import { cleanQueryString, setQueryString } from '@portal/services/queryString';
import AdvancedRangePicker from '@portal/components/AdvancedRangePicker/AdvancedRangePicker';
import { DateTime } from 'luxon';
import AdvancedDateTimePicker from '@portal/components/AdvancedDateTimePicker/AdvancedDateTimePicker';
import { TextField } from '@material-ui/core';
import { Autocomplete } from '@mui/material';
import AdvancedForm from '@portal/components/AdvancedForm/AdvancedForm';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import './AdvancedModalFilters.less';

type ButtonType = {
  onClick?: () => void;
  disabled?: boolean;
  text?: string;
  icon?: ReactElement;
  hide?: boolean;
};

type AdvancedModalFiltersType = {
  fields?: FiltersFields[];
  onSearch: (filters) => void;
  defaultFields?: advancedFilterModels.baseFilter;
  disabled?: boolean;
  refreshPage?: () => void;
  oneButton?: ButtonType;
  oneFunction?: ButtonType;
};

export const defaultFields = {
  page: 0,
  offset: 0,
  limit: 10,
  orderBy: 'createdAt',
  sort: 'DESC',
};

type FormValue = string | number | boolean;

const AdvancedModalFilters = ({
  fields,
  onSearch,
  defaultFields,
  disabled,
  refreshPage,
  oneButton,
  oneFunction,
}: AdvancedModalFiltersType): JSX.Element => {
  const [dateRange, setDateRange] = useState<Moment[] | null>();
  const filterParams = new URLSearchParams(location.search);
  const [queryPage, setQueryPage] = useState(filterParams.get('page') ?? '');
  const [selectedOption, setSelectedOption] = useState<SelectOptions | null>(
    null
  );
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (onSearch && form) {
      onSearch(cleanResponse(form));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDateRangeDefaultValue = (date: string | null) => {
    if (date) {
      const newFormatStartDate = moment(date.split(',')[0]);
      const newFormatEndDate = moment(date.split(',')[1]);

      setDateRange([newFormatStartDate.utc(), newFormatEndDate.utc()]);

      return [newFormatStartDate, newFormatEndDate];
    } else {
      return null;
    }
  };

  const formatField = (field, cleanSearch, dateRange) => {
    if (cleanSearch) {
      return {
        [field.name]: '',
      };
    }

    if (field.name === 'dateRange' && !dateRange) {
      const newDate = setDateRangeDefaultValue(field.defaultValue);
      const firstDay = newDate ? newDate[0].toDate().toISOString() : '';
      const lastDay = newDate ? newDate[1].toDate().toISOString() : '';
      return {
        dateRange: field.defaultValue ? `${firstDay},${lastDay}` : '',
      };
    }

    return {
      [field.name]: field.defaultValue ?? '',
    };
  };

  const parseValues = (cleanSearch?: boolean) => {
    const formattedFields = fields?.map((field) =>
      formatField(field, cleanSearch, dateRange)
    );

    const result = Object.fromEntries(
      formattedFields ? formattedFields.map(Object.entries) : []
    );

    return result;
  };

  const [form, setForm] = useState(parseValues());

  const onFormChange = (key: string, value: FormValue) => {
    const updatedForm = {
      ...form,
      [key]: value,
    };
    setForm(updatedForm);
  };

  const onCleanSearch = () => {
    cleanQueryString();
    setSelectedOption(null);
    setDateRange([]);

    const defaultValues = parseValues(true);

    const searchDefaults = {
      page: 0,
      pageSize: 10,
      ...defaultValues,
    };

    setForm(defaultValues);

    onSearch(searchDefaults);
  };

  const cleanResponse = (params: Record<string, FormValue>) => {
    cleanQueryString();

    for (const propName in params) {
      if (
        params[propName] === null ||
        params[propName] === undefined ||
        params[propName] === ''
      ) {
        delete params[propName];
      }
    }

    params = {
      ...defaultFields,
      ...params,
      page: !Number.isNaN(parseInt(queryPage, 10))
        ? parseInt(queryPage, 10)
        : defaultFields?.page ?? 0,
    } as unknown as Record<string, FormValue>;

    cleanSearchFields(params);

    setQueryString(params);
    setQueryPage('0');
    setShowModal(false);
    return params;
  };

  const cleanSearchFields = (params: Record<string, FormValue>) => {
    for (const field in fields) {
      const fieldIndex = parseInt(field, 10);
      const { cleanOnSearch, name, type } = fields[fieldIndex];
      if (cleanOnSearch) {
        const value = params[name];
        if (value && type !== AdvancedFilterType.RADIO) {
          params[name] = removeSpecialChars(`${value}`);
        }
      }
    }
  };

  const handleSelectValue = (value?: FormValue) => {
    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }
  };

  const handleChangeDatePiker = (
    key: string,
    value: Array<Moment | null> | null
  ) => {
    if (value?.[0] && value[1]) {
      const firstDay = value[0].utc(true).startOf('day');
      const lastDay = value[1].utc(true).endOf('day');

      const updatedForm = {
        ...form,
        createdAtStart: `${firstDay.toDate().toISOString()}`,
        createdAtEnd: `${lastDay.toDate().toISOString()}`,
      };
      setDateRange([firstDay, lastDay]);
      setForm(updatedForm);
    }
  };

  const handleOnFormChange = (name: string, value: unknown) => {
    if (typeof value === 'string' || typeof value === 'number') {
      onFormChange(name, value);
    }
  };

  const handleChangeDatePikerFree = (
    key: string,
    value: Array<Moment | null> | null
  ) => {
    if (value && value[0] && value[1]) {
      const firstDay = value[0].startOf('day').utc(true);
      const lastDay = value[1].endOf('day').utc(true);

      const updatedForm = {
        ...form,
        [key]: `${firstDay.toDate().toISOString()},${lastDay
          .toDate()
          .toISOString()}`,
      };
      setForm(updatedForm);
    }
  };

  return (
    <>
      <div className="advanced-modal-filters__row">
        <AdvancedButton
          text={translate('COMPONENTS.ADVANCED_FILTER.CLEAN')}
          variant="outlined"
          endIcon={<ClearOutlined />}
          onClick={() => onCleanSearch()}
        />
        <AdvancedButton
          text={translate('COMPONENTS.ADVANCED_FILTER.FILTER')}
          variant="outlined"
          endIcon={<FilterOutlined />}
          onClick={() => setShowModal(true)}
        />
        {oneFunction && oneFunction?.hide ? (
          <AdvancedButton
            text={oneFunction.text ?? ''}
            endIcon={oneFunction.icon}
            onClick={oneFunction.onClick}
            disabled={oneFunction.disabled}
            variant="contained"
          />
        ) : null}
        {oneButton ? (
          <AdvancedButton
            text={oneButton.text ?? ''}
            endIcon={oneButton.icon}
            onClick={oneButton.onClick}
            disabled={oneButton.disabled}
            variant="contained"
          />
        ) : null}
        {refreshPage ? (
          <AdvancedButton
            text={translate('APPLICATION.REFRESH')}
            endIcon={<ReloadOutlined />}
            onClick={refreshPage}
            variant="contained"
          />
        ) : null}
      </div>
      <GenericModal
        visible={showModal}
        onClose={() => setShowModal(false)}
        size="small"
        title={translate('COMPONENTS.ADVANCED_FILTER.FILTER')}
      >
        <AdvancedForm
          onSubmit={() => (form ? onSearch(cleanResponse(form)) : {})}
        >
          <div className="advanced-modal-filters__inputs">
            {fields?.map((field) => (
              <div key={field.name} className="advanced-filter__fields__single">
                {field.type === AdvancedFilterType.AUTO_COMPLETE &&
                  !field.hide && (
                    <div className="advanced-filter__fields__single__inner">
                      <Autocomplete
                        disableClearable={!field.cleanAutoComplete}
                        options={field.options ?? []}
                        getOptionLabel={(option: { name: string }) =>
                          option.name
                        }
                        value={selectedOption}
                        onChange={(event, value) => {
                          if (field.autoCompleteOnChange) {
                            field.autoCompleteOnChange(event, value);
                          }
                          onFormChange(field.name, value ? value.value : '');
                          setSelectedOption(value);
                        }}
                        onInputChange={field.onInputChange}
                        renderInput={(inputParams) => (
                          <div className="advanced-input">
                            <TextField
                              {...inputParams}
                              label={field.placeholder}
                              variant="outlined"
                              InputProps={{
                                ...inputParams.InputProps,
                                type: 'search',
                              }}
                            />
                          </div>
                        )}
                      />
                    </div>
                  )}
                {field.type === AdvancedFilterType.TEXT && !field.hide && (
                  <div className="advanced-filter__fields__single__inner">
                    <AdvancedInput
                      disabled={field.disableFuture}
                      label={
                        typeof field.placeholder === 'string'
                          ? field.placeholder
                          : translate(
                              'COMPONENTS.ADVANCED_FILTER.DEFAULT_WRITE_PLACEHOLDER'
                            )
                      }
                      onChange={(val: string) =>
                        onFormChange(
                          field.name,
                          field.formatter ? unmaskField(val) : val
                        )
                      }
                      value={
                        field.formatter && form && form[field.name]
                          ? field.formatter(`${form[field.name]}`)
                          : form?.[field.name]
                      }
                      maxLength={field.maxLength}
                    />
                  </div>
                )}
                {field.type === AdvancedFilterType.DATE_PICKER &&
                  !field.hide && (
                    <div className="advanced-filter__fields__single__inner">
                      <AdvancedDateTimePicker
                        label={
                          typeof field.placeholder === 'string'
                            ? field.placeholder
                            : translate(
                                'COMPONENTS.ADVANCED_FILTER.DEFAULT_PICK_PLACEHOLDER'
                              )
                        }
                        onChange={(val: DateTime) =>
                          onFormChange(field.name, val?.toISO())
                        }
                        value={form?.[field.name]?.toString() || null}
                        disableFuture={field.disableFuture}
                        onlyDate={field.onlyDate}
                        minDate={field.minDate}
                      />
                    </div>
                  )}
                {field.type === AdvancedFilterType.SELECT && !field.hide && (
                  <div className="advanced-filter__fields__single__inner">
                    <AdvancedSelect
                      label={
                        typeof field.placeholder === 'string'
                          ? field.placeholder
                          : translate(
                              'COMPONENTS.ADVANCED_FILTER.DEFAULT_PICK_PLACEHOLDER'
                            )
                      }
                      onChange={(value: string | number | boolean) => {
                        handleOnFormChange(field.name, value);
                        if (field.getValues) {
                          field.getValues(value);
                        }
                      }}
                      value={handleSelectValue(form?.[field.name])}
                      options={field.options}
                    />
                  </div>
                )}
                {field.type === AdvancedFilterType.RANGE_PICKER &&
                  !field.hide && (
                    <div className="advanced-filter__fields__single__inner">
                      {field.freeField ? (
                        <AdvancedRangePicker
                          limitRange={field.limitRangePicker}
                          rangeLimitValue={field.rangeLimitValue}
                          rangeLimitUnit={field.rangeLimitUnit}
                          placeholder={
                            Array.isArray(field.placeholder)
                              ? field.placeholder
                              : [
                                  translate(
                                    'COMPONENTS.ADVANCED_FILTER.DEFAULT_PICK_PLACEHOLDER'
                                  ),
                                  translate(
                                    'COMPONENTS.ADVANCED_FILTER.DEFAULT_PICK_PLACEHOLDER'
                                  ),
                                ]
                          }
                          onChange={(val: Array<Moment | null> | null) =>
                            handleChangeDatePikerFree(field.name, val)
                          }
                          value={
                            form && form[field.name]
                              ? [
                                  moment(
                                    form[field.name].toString().split(',')[0]
                                  ).utc(false),
                                  moment(
                                    form[field.name].toString().split(',')[1]
                                  ).utc(false),
                                ]
                              : null
                          }
                        />
                      ) : (
                        <AdvancedRangePicker
                          limitRange={field.limitRangePicker}
                          rangeLimitValue={field.rangeLimitValue}
                          rangeLimitUnit={field.rangeLimitUnit}
                          placeholder={
                            Array.isArray(field.placeholder)
                              ? field.placeholder
                              : [
                                  translate(
                                    'COMPONENTS.ADVANCED_FILTER.DEFAULT_PICK_PLACEHOLDER'
                                  ),
                                  translate(
                                    'COMPONENTS.ADVANCED_FILTER.DEFAULT_PICK_PLACEHOLDER'
                                  ),
                                ]
                          }
                          onChange={(val: Array<Moment | null> | null) =>
                            handleChangeDatePiker(field.name, val)
                          }
                          value={
                            dateRange ? [dateRange[0], dateRange[1]] : null
                          }
                        />
                      )}
                    </div>
                  )}
              </div>
            ))}
          </div>
          <div className="advanced-filter__search">
            <div className="advanced-filter__search__clean">
              <AdvancedButton
                text={translate('COMPONENTS.ADVANCED_FILTER.CLEAN')}
                onClick={onCleanSearch}
                variant="outlined"
                endIcon={<ClearOutlined />}
                disabled={disabled}
              />
            </div>
            <AdvancedButton
              text={translate('COMPONENTS.ADVANCED_FILTER.FILTER')}
              endIcon={<SearchOutlined />}
              type="submit"
              customStyles={{ borderRadius: 4 }}
              disabled={disabled}
            />
          </div>
        </AdvancedForm>
      </GenericModal>
    </>
  );
};

export default AdvancedModalFilters;
